// Contenido del portal de facturación de la suscripción (solo AR) — estilo portal
// de Stripe, diseño Atlas. Gestión COMPLETA in-place (4 bloques):
//   1) Tu suscripción   → plan + precio/mes (config de planes) + próximo pago + tarjeta.
//                          [Actualizar suscripción] → reusa el Paywall (/subscription).
//   2) Método de pago   → tarjeta con LOGO de red + cambio de tarjeta en Rebill (solo clientes Rebill).
//   3) Datos fiscales   → AFIP, editable (CustomSelect estilo Atlas), guarda REAL a Supabase.
//   4) Historial        → lista → abre la sub-pantalla /facturacion/factura/:id.
// `demo` rellena datos de ejemplo para ver todo cargado. Ver ADR-2026-08-03-facturacion-*.
// El upgrade real vive en /subscription → /checkout (Rebill), no acá. Acá no se cancela la
// suscripción: el botón placeholder (sin backend) se sacó en
// ADR-2026-11-03-facturacion-sin-cancelar-suscripcion.
//
// Facturación por marca (`company_profiles.facturacion_por_marca`, leída con `lib/saldo`): la
// empresa no tiene suscripción, cada marca tiene la suya. Los bloques (1) y (2) se cambian por
// los planes de cada marca (`PlanPorMarca`, la misma de Configuración → Plan) y cada factura
// dice de qué marca es. Sin saber la bandera no se muestra nada de la suscripción de la empresa.
// Ver ADR-2026-10-29-facturacion-con-planes-por-marca.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, FileText, Loader2, Pencil, ChevronRight, Search, CheckCircle2, BadgeCheck, CreditCard, CalendarDays } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CustomSelect } from '../../components/CustomSelect';
import { CardBrandIcon, detectCardBrand } from '../../components/CardBrandIcon';
import OptionSheet from '../../components/opciones/OptionSheet';
import { CONDICIONES_IVA, validarCUIT, formatCUIT } from '../../lib/facturacion';
import { getPlanById } from '../../lib/checkoutPlans';
import { FACTURAS_MOCK } from '../../lib/facturacionMock';
import { facturarPendientes, consultarPadron, fetchRebillCardUpdateLink } from '../../lib/paymentServer';
import { nombreDelPlanDeEquipo } from '../../lib/catalogoDePlanesDeEquipo';
import { asegurarSaldoLeido, ultimoSaldoLeido } from '../../lib/saldo';
import { useUltimoSaldo } from '../../hooks/useSaldo';
import {
  bloqueDeSuscripcion,
  conMarcaDeCadaPago,
  hayQueCargarMarcasDeFacturas,
  marcaDeCadaPago,
} from '../../components/facturacionPorMarca/planesPorMarca';
import PlanPorMarca from './PlanPorMarca';

// Item unificado del historial (sirve tanto para el mock del demo como para las facturas reales).
type FacturaItem = {
  id: string;
  fecha: string; // ISO / YYYY-MM-DD
  tipo: string; // letra A/B/C
  numero: string;
  total: number;
  estado: string;
  plan?: string | null;
  /** Marca del pago (sólo con facturación por marca). */
  marca?: string | null;
  /** El pago de la factura: de ahí sale su marca. */
  paymentId?: string | null;
};

/**
 * Con facturación por marca, la marca de cada pago: payment_id → payments.brand_id →
 * brand_profiles.name. Si algo falla, vacío: las facturas salen sin marca (como sin bandera).
 */
async function leerMarcasDeLosPagos(idsDePago: string[]): Promise<Map<string, string>> {
  try {
    const ids = [...new Set(idsDePago.filter(Boolean))];
    if (ids.length === 0) return new Map();
    const { data: pagos } = await supabase.from('payments').select('id, brand_id').in('id', ids);
    const pagosConMarca = (pagos ?? []) as { id: string; brand_id: string | null }[];
    const idsDeMarca = [...new Set(pagosConMarca.map((p) => p.brand_id).filter((b): b is string => !!b))];
    if (idsDeMarca.length === 0) return new Map();
    const { data: marcas } = await supabase.from('brand_profiles').select('id, name').in('id', idsDeMarca);
    return marcaDeCadaPago(pagosConMarca, (marcas ?? []) as { id: string; name: string }[]);
  } catch {
    return new Map();
  }
}

interface Props {
  profile: { id_company: string | null } | null;
  demo?: boolean;
}

interface DatosFacturacion {
  cuit: string;
  razon_social: string;
  condicion_iva: string;
  email_facturacion?: string | null;
}

interface Suscripcion {
  plan: string | null;
  proximaFactura: string | null; // ISO
}

interface MetodoPago {
  tipo: string | null; // ej. "Mastercard"
  last4: string | null;
  venc: string | null; // ej. "08/2029" (si el backend lo provee)
}

// Nombres de plan (alineados con Plan.tsx / checkoutPlans). El precio sale de getPlanById.
const PLAN_NOMBRES: Record<string, string> = {
  gratis: 'Prueba Gratis',
  emprendimiento: 'Básico',
  negocio: 'Pro',
  enterprise: 'Ultra',
  enterprise1: 'Ultra Nivel 1',
  enterprise2: 'Ultra Nivel 2',
  enterprise3: 'Ultra Nivel 3',
  enterprise_plus: 'Empresa',
};

const MONEDA = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

// Parsea una fecha de la base sin el corrimiento de día por timezone: las fechas date-only
// ('YYYY-MM-DD', ej. fecha_comprobante) se interpretan como LOCAL, no UTC. Si no, `new
// Date('2026-08-04')` es medianoche UTC y en Argentina (UTC-3) se muestra el día anterior.
function parseFechaLocal(s: string | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
const fmtFecha = (iso: string | null) => {
  const d = parseFechaLocal(iso);
  return d ? d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};
const fmtFechaLarga = (iso: string | null) => {
  const d = parseFechaLocal(iso);
  return d ? d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
};

// Próxima factura = próxima ocurrencia del día ancla (paid_plan_start_date), o del
// último pago + 1 mes como fallback. Igual criterio que la sección "Plan".
function calcProximaFactura(startISO?: string | null, lastISO?: string | null): string | null {
  // parseFechaLocal por consistencia con el resto del archivo. (Estas columnas son timestamptz
  // —traen hora—, así que hoy no hay corrimiento; pero si alguna vez llegaran date-only
  // 'YYYY-MM-DD', `new Date` las tomaría como UTC y el ancla se correría un día en AR.)
  const base = startISO ? parseFechaLocal(startISO) : lastISO ? parseFechaLocal(lastISO) : null;
  if (!base || isNaN(base.getTime())) return null;
  const now = new Date();
  const anchorDay = base.getDate();
  // Clamp al último día del mes: si el ancla es 29/30/31 y el mes destino es más corto,
  // `new Date(y, m, 31)` se desborda al mes siguiente (feb 31 → mar 3). Lo evitamos.
  const buildDate = (year: number, month: number) => {
    const lastDay = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(anchorDay, lastDay));
  };
  let next = buildDate(now.getFullYear(), now.getMonth());
  if (next.getTime() <= now.getTime()) next = buildDate(now.getFullYear(), now.getMonth() + 1);
  return next.toISOString();
}

export function FacturacionSuscripcion({ profile, demo = false }: Props) {
  const idCompany = profile?.id_company ?? null;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState<Suscripcion | null>(null);
  const [metodo, setMetodo] = useState<MetodoPago | null>(null);
  const [datos, setDatos] = useState<DatosFacturacion | null>(null);
  // Datos fiscales que trajimos de Tiendanube (CUIT + razón social) para PRE-LLENAR el form cuando el
  // usuario todavía no cargó los suyos (Fase 4, solo AR). El token de la tienda nunca llega acá.
  const [tnFiscal, setTnFiscal] = useState<{ cuit: string | null; razon: string | null } | null>(null);
  // Fallback de precio: el monto del último pago ARS, para planes que no están en checkoutPlans.
  const [ultimoPagoMonto, setUltimoPagoMonto] = useState<number | null>(null);
  // `plataforma_pago` de la empresa: el botón "Actualizar medio de pago" (que va a Rebill) solo
  // aplica a clientes de Rebill; a los de MP/otros no se les muestra. Ver ADR-0024 (payment-server).
  const [plataformaPago, setPlataformaPago] = useState<string | null>(null);
  // true mientras pedimos el link de Rebill (evita doble click; en éxito la página redirige sola).
  const [actualizandoTarjeta, setActualizandoTarjeta] = useState(false);
  // Facturación por marca, leída con `lib/saldo` antes de dejar de cargar. `null` = no se pudo
  // saber; si después llega una lectura (la del shell, o la que se pasó del tope), se toma esa.
  const [porMarcaLeido, setPorMarcaLeido] = useState<boolean | null>(null);
  const ultimoSaldo = useUltimoSaldo();

  const [sheetOpen, setSheetOpen] = useState(false); // form datos fiscales
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState(''); // buscador del historial
  const [facturasReales, setFacturasReales] = useState<FacturaItem[]>([]);
  // ¿Ya se pidieron las marcas de las facturas con la bandera prendida? Si la bandera llega
  // después de la carga (`useUltimoSaldo`), el efecto de más abajo las pide.
  const [marcasPedidas, setMarcasPedidas] = useState(false);
  // Aviso tras activar/guardar los datos: le contamos al usuario qué pasó con la emisión
  // (no dejar el flujo mudo si algo falla o si todavía se está procesando).
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'info' | 'warn'; texto: string } | null>(null);

  // Form datos fiscales
  const [cuit, setCuit] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [condicionIva, setCondicionIva] = useState<string>(CONDICIONES_IVA[0]);
  const [email, setEmail] = useState('');
  // Estado de la consulta al padrón AFIP (autocompletado por CUIT).
  const [padron, setPadron] = useState<'idle' | 'buscando' | 'ok' | 'no'>('idle');

  // Historial: en demo, el mock; en real, lo que trae el backend (tabla facturas_suscripcion).
  const facturasBase: FacturaItem[] = demo
    ? FACTURAS_MOCK.map((f) => ({
        id: f.id,
        fecha: f.fecha,
        tipo: f.tipo,
        numero: f.numero,
        total: f.total,
        estado: f.estado,
        plan: f.plan,
      }))
    : facturasReales;
  const facturas = facturasBase.filter(
    (f) =>
      !q.trim() ||
      f.numero.includes(q.trim()) ||
      fmtFecha(f.fecha).toLowerCase().includes(q.trim().toLowerCase()) ||
      !!f.marca?.toLowerCase().includes(q.trim().toLowerCase()),
  );

  const fetchTodo = async () => {
    // Modo demo: datos de ejemplo para ver el portal "con todo cargado".
    if (demo) {
      setSub({ plan: 'negocio', proximaFactura: calcProximaFactura('2026-01-27') });
      setMetodo({ tipo: 'Mastercard', last4: '0530', venc: '08/2029' });
      setDatos({
        cuit: '20409378472',
        razon_social: 'Imanol Castro',
        condicion_iva: 'Monotributista',
        email_facturacion: 'imacastro06@gmail.com',
      });
      setLoading(false);
      return;
    }
    if (!idCompany) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMarcasPedidas(false);
    // ¿Factura por marca? El último saldo leído, o una lectura (con tope: nunca queda cargando),
    // en paralelo con lo de abajo. La bandera es de la empresa: un saldo de otra marca la sigue
    // diciendo bien. Se espera antes de dejar de cargar: la card de la empresa nunca se asoma.
    const bandera = asegurarSaldoLeido({ topeMs: 8000 }).then(
      (saldo) => saldo?.facturaPorMarca ?? ultimoSaldoLeido()?.facturaPorMarca ?? null,
    );
    // Suscripción (company_profiles) — solo lectura, sin tocar pagos.
    const { data: company } = await supabase
      .from('company_profiles')
      .select('plan, paid_plan_start_date, paid_plan_last_payment_date, plataforma_pago, tn_business_id, tn_business_name')
      .eq('id_company', idCompany)
      .maybeSingle();
    if (company) {
      const c = company as {
        plan?: string | null;
        paid_plan_start_date?: string | null;
        paid_plan_last_payment_date?: string | null;
        plataforma_pago?: string | null;
        tn_business_id?: string | null;
        tn_business_name?: string | null;
      };
      // Datos fiscales de la tienda (Tiendanube) para pre-llenar el form si aún no cargó los suyos.
      setTnFiscal({ cuit: c.tn_business_id ?? null, razon: c.tn_business_name ?? null });
      setSub({
        plan: c.plan ?? null,
        proximaFactura: calcProximaFactura(c.paid_plan_start_date, c.paid_plan_last_payment_date),
      });
      setPlataformaPago(c.plataforma_pago ?? null);
    }
    // Método de pago (último pago ARS) — resiliente si RLS/no hay data.
    const { data: pago } = await supabase
      .from('payments')
      .select('metodo_pago, last4digits, cantidad')
      .eq('id_company', idCompany)
      .eq('moneda', 'ARS')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (pago) {
      const p = pago as { metodo_pago?: string | null; last4digits?: string | null; cantidad?: number | string | null };
      setMetodo({ tipo: p.metodo_pago ?? null, last4: p.last4digits ?? null, venc: null });
      const monto = Number(p.cantidad);
      setUltimoPagoMonto(Number.isFinite(monto) && monto > 0 ? monto : null);
    }
    // Datos fiscales.
    const { data: df, error: dfErr } = await supabase
      .from('datos_facturacion_suscripcion')
      .select('cuit, razon_social, condicion_iva, email_facturacion')
      .eq('id_company', idCompany)
      .maybeSingle();
    setDatos(dfErr || !df ? null : (df as DatosFacturacion));
    // Historial real (facturas_suscripcion). Resiliente: si la tabla aún no existe / RLS, queda vacío.
    const { data: facts } = await supabase
      .from('facturas_suscripcion')
      .select('id, payment_id, fecha_comprobante, letra, punto_venta, numero_comprobante, total, estado')
      .eq('id_company', idCompany)
      .eq('estado', 'emitida')
      .order('created_at', { ascending: false });
    const porMarca = await bandera;
    setPorMarcaLeido(porMarca);
    // Con facturación por marca, la marca de cada factura (si algo falla, sale sin marca).
    const marcaDelPago =
      porMarca === true && facts && facts.length > 0
        ? await leerMarcasDeLosPagos(facts.map((f) => (f as { payment_id: string }).payment_id))
        : new Map<string, string>();
    setMarcasPedidas(porMarca === true);
    setFacturasReales(
      (facts ?? []).map((r) => {
        const row = r as {
          id: string;
          payment_id: string;
          fecha_comprobante: string | null;
          letra: string | null;
          punto_venta: number | null;
          numero_comprobante: number | null;
          total: number | string | null;
          estado: string;
        };
        return {
          id: row.id,
          fecha: row.fecha_comprobante ?? '',
          tipo: row.letra ?? '',
          numero: `${String(row.punto_venta ?? 0).padStart(4, '0')}-${String(row.numero_comprobante ?? 0).padStart(8, '0')}`,
          total: Number(row.total ?? 0),
          estado: row.estado,
          plan: null,
          marca: marcaDelPago.get(row.payment_id) ?? null,
          paymentId: row.payment_id ?? null,
        };
      }),
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCompany, demo]);

  // La bandera llegó prendida DESPUÉS de armar el historial sin marcas (la primera lectura del
  // saldo tardó de más): se piden las marcas de esas facturas. Una sola vez por carga.
  const facturaPorMarcaVigente = porMarcaLeido ?? ultimoSaldo?.facturaPorMarca ?? null;
  const idsDePagoDelHistorial = facturasReales.map((f) => f.paymentId).filter((p): p is string => !!p);
  const cargarMarcasDeFacturas =
    !demo &&
    hayQueCargarMarcasDeFacturas({
      facturaPorMarca: facturaPorMarcaVigente,
      cargando: loading,
      marcasPedidas,
      idsDePago: idsDePagoDelHistorial,
    });
  useEffect(() => {
    if (!cargarMarcasDeFacturas) return;
    setMarcasPedidas(true);
    void leerMarcasDeLosPagos(idsDePagoDelHistorial).then((marcaDelPago) => {
      if (marcaDelPago.size > 0) setFacturasReales((prev) => conMarcaDeCadaPago(prev, marcaDelPago));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargarMarcasDeFacturas]);

  // Autocompletado por padrón AFIP: al tipear un CUIT válido, buscamos razón social + condición.
  useEffect(() => {
    if (!sheetOpen || !validarCUIT(cuit)) {
      setPadron('idle');
      return;
    }
    let cancelado = false;
    setPadron('buscando');
    const t = setTimeout(async () => {
      const r = await consultarPadron(cuit);
      if (cancelado) return;
      if (!r.ok) {
        setPadron('idle'); // no se pudo verificar (backend no activo) → no bloqueamos el form
        return;
      }
      if (r.exists) {
        setPadron('ok');
        // No pisar lo que el usuario ya escribió: solo autocompletamos la razón social si
        // está vacía (si la escribió a mano, la respetamos).
        if (r.razonSocial) setRazonSocial((prev) => (prev.trim() ? prev : r.razonSocial!));
        if (r.condicionIva && (CONDICIONES_IVA as readonly string[]).includes(r.condicionIva)) {
          setCondicionIva(r.condicionIva);
        }
      } else {
        setPadron('no');
      }
    }, 500);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuit, sheetOpen]);

  const openForm = () => {
    // Si el usuario ya cargó sus datos, esos mandan; si no, pre-llenamos con los de su Tiendanube
    // (Fase 4). El CUIT se normaliza a solo dígitos por si la tienda lo trae con guiones.
    setCuit((datos?.cuit ?? tnFiscal?.cuit ?? '').replace(/\D/g, ''));
    setRazonSocial(datos?.razon_social ?? tnFiscal?.razon ?? '');
    setCondicionIva(datos?.condicion_iva ?? CONDICIONES_IVA[0]);
    setEmail(datos?.email_facturacion ?? '');
    setSheetOpen(true);
  };

  const cuitValido = validarCUIT(cuit);
  const formValido = cuitValido && razonSocial.trim().length > 1;

  const guardar = async () => {
    if (!idCompany || !formValido || saving) return;
    setSaving(true);
    setAviso(null);
    const { error } = await supabase.from('datos_facturacion_suscripcion').upsert(
      {
        id_company: idCompany,
        cuit: cuit.replace(/\D/g, ''),
        razon_social: razonSocial.trim(),
        condicion_iva: condicionIva,
        email_facturacion: email.trim() || null,
        activo: true,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id_company' },
    );
    if (error) {
      setSaving(false);
      setAviso({ tipo: 'warn', texto: 'No pudimos guardar tus datos. Probá de nuevo en unos segundos.' });
      return;
    }
    setSheetOpen(false);
    // Opt-in: al cargar los datos, disparamos la facturación de los pagos pendientes del
    // usuario. Le damos feedback del resultado (no dejar el flujo mudo si algo falla).
    const { data: auth } = await supabase.auth.getUser();
    const res: { ok: boolean; emitidas?: number; errores?: number } = auth.user
      ? await facturarPendientes(auth.user.id)
      : { ok: false };
    await fetchTodo();
    setSaving(false);
    if (res.ok && (res.emitidas ?? 0) > 0) {
      const n = res.emitidas ?? 0;
      setAviso({
        tipo: 'ok',
        texto:
          n === 1
            ? 'Listo, generamos tu factura. Ya la ves en el historial.'
            : `Listo, generamos tus ${n} facturas. Ya las ves en el historial.`,
      });
    } else if (res.ok && (res.errores ?? 0) > 0) {
      setAviso({
        tipo: 'warn',
        texto: 'Guardamos tus datos. Tu factura se está generando y puede demorar unos minutos en aparecer.',
      });
    } else {
      setAviso({
        tipo: 'info',
        texto: 'Guardamos tus datos. Tus facturas van a aparecer acá con cada pago.',
      });
    }
  };

  // Cambiar la tarjeta: le pedimos a Rebill un link hosteado y redirigimos ahí. NO cobra (es un
  // reemplazo de tarjeta). Solo para clientes Rebill (ver `esRebill`). Ver ADR-0024 (payment-server).
  const actualizarTarjeta = async () => {
    if (actualizandoTarjeta) return;
    // En modo demo NO llamamos al endpoint real (el usuario podría no ser cliente Rebill): mostramos
    // qué haría, sin pegarle a la API en vivo con su user_id. Greptile PR#68.
    if (demo) {
      setAviso({ tipo: 'info', texto: 'Modo demo: acá se abre la página de Rebill para cambiar la tarjeta.' });
      return;
    }
    setActualizandoTarjeta(true);
    setAviso(null);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error('Cerrá sesión, volvé a entrar y probá de nuevo.');
      const url = await fetchRebillCardUpdateLink(auth.user.id);
      window.location.href = url; // redirige a la página hosteada de Rebill (navega sola)
    } catch (e) {
      setActualizandoTarjeta(false);
      setAviso({
        tipo: 'warn',
        texto:
          e instanceof Error ? e.message : 'No pudimos abrir el cambio de tarjeta. Probá de nuevo.',
      });
    }
  };

  // Los avisos de `PlanPorMarca` van al aviso del portal. Estables: son dependencia de su carga.
  const avisoDeError = useCallback((e: string | null) => setAviso(e ? { tipo: 'warn', texto: e } : null), []);
  const avisoDeExito = useCallback((t: string | null) => setAviso(t ? { tipo: 'ok', texto: t } : null), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  const planInfo = sub?.plan ? getPlanById(sub.plan) : undefined;
  const planNombre =
    (sub?.plan ? PLAN_NOMBRES[sub.plan] ?? nombreDelPlanDeEquipo(sub.plan) : null) ?? planInfo?.name ?? (sub?.plan ?? null);
  // Precio del plan (checkoutPlans); si el plan no está ahí, caemos al monto del último pago.
  const precioMensual = planInfo?.price ?? ultimoPagoMonto ?? null;
  // El precio del plan es FINAL (IVA incluido). Desglose para mostrar el neto y el
  // IVA (lo que va a ARCA) — pedido de Mati. Ver ADR-2026-08-03-facturacion-*.
  const netoMensual = precioMensual != null ? Math.round(precioMensual / 1.21) : null;
  const ivaMensual = precioMensual != null && netoMensual != null ? precioMensual - netoMensual : null;
  // Nombre de la red con mayúscula inicial ("mastercard" → "Mastercard", "american express" →
  // "American Express"). El dato viene de payments.metodo_pago, normalmente en minúscula.
  const tituloRed = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const tarjetaLabel = metodo?.last4
    ? `${metodo.tipo ? tituloRed(metodo.tipo) : 'Tarjeta'} ···· ${metodo.last4}`
    : null;
  const brandGuardada = detectCardBrand(metodo?.tipo || metodo?.last4 || '');
  // "Método de pago" ahora es FUNCIONAL para clientes Rebill (botón → cambio de tarjeta hosteado en
  // Rebill, sin re-cobrar). Se muestra a los de Rebill (o en demo); los de MP/otros no lo ven porque
  // el link de Rebill no les aplica. Ver ADR-0024.
  const esRebill = demo || plataformaPago === 'rebill';
  // Con facturación por marca no hay suscripción de la empresa: ni su card, ni "Actualizar" (vendería
  // un plan de empresa que nadie usa) ni el cambio de tarjeta (va a la suscripción
  // Rebill de la empresa). Sin saber la bandera, tampoco. Ver ADR-2026-10-29-facturacion-con-planes-por-marca.
  const bloque = bloqueDeSuscripcion({ demo, facturaPorMarca: porMarcaLeido ?? ultimoSaldo?.facturaPorMarca ?? null });
  const deEmpresa = bloque === 'empresa';

  return (
    <div>
      {aviso && (
        <div
          className={`mb-4 flex items-start gap-2 rounded-xl px-4 py-3 text-[13.5px] ${
            aviso.tipo === 'ok'
              ? 'bg-success-soft text-success-text'
              : aviso.tipo === 'warn'
                ? 'bg-warning-soft text-warning-text'
                : 'bg-neutral-100 text-neutral-600'
          }`}
        >
          {aviso.tipo === 'ok' ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <Receipt className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span>{aviso.texto}</span>
        </div>
      )}

      {/* 1 · Planes de cada marca (facturación por marca), en lugar de la suscripción de la empresa */}
      {bloque === 'marcas' && idCompany && (
        <Bloque
          titulo="Planes de tus marcas"
          descripcion="Cada marca tiene su propio plan y sus propios créditos."
          icono={<BadgeCheck className="h-[18px] w-[18px]" />}
        >
          <PlanPorMarca idCompany={idCompany} setError={avisoDeError} setSuccess={avisoDeExito} conEncabezado={false} />
        </Bloque>
      )}

      {bloque === 'sin_saber' && (
        <Bloque titulo="Tu suscripción" icono={<BadgeCheck className="h-[18px] w-[18px]" />}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[14px] text-neutral-600">No pudimos cargar tu plan. Probá de nuevo en unos segundos.</p>
            <button onClick={() => void fetchTodo()} className={BTN_OUTLINE}>
              Reintentar
            </button>
          </div>
        </Bloque>
      )}

      {/* 1 · Tu suscripción */}
      {deEmpresa && (
        <Bloque titulo="Tu suscripción" icono={<BadgeCheck className="h-[18px] w-[18px]" />}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] px-2.5 py-0.5 text-[12px] font-semibold text-brand-primary">
                Plan {planNombre || '—'}
              </span>
              {precioMensual != null && (
                <p className="mt-2 text-[30px] font-semibold leading-none tracking-[-0.02em] text-brand-black tabular-nums">
                  {MONEDA.format(precioMensual)}{' '}
                  <span className="text-[14px] font-normal tracking-normal text-neutral-500">por mes</span>
                </p>
              )}
              {netoMensual != null && ivaMensual != null && (
                <p className="mt-2 text-[12.5px] text-neutral-500 tabular-nums">
                  Neto {MONEDA.format(netoMensual)} · IVA (21%) {MONEDA.format(ivaMensual)} <span className="text-neutral-400">· IVA incluido</span>
                </p>
              )}
              {sub?.proximaFactura && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-neutral-600">
                  <CalendarDays className="h-4 w-4 text-neutral-400" /> Tu próximo pago es el {fmtFechaLarga(sub.proximaFactura)}.
                </p>
              )}
              {tarjetaLabel && (
                <div className="flex items-center gap-2 mt-2.5 text-[13.5px] text-brand-black/80">
                  <CardBrandIcon brand={brandGuardada} /> {tarjetaLabel}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-auto shrink-0">
              <button onClick={() => navigate('/subscription')} className={BTN_PRIMARY}>
                Actualizar suscripción
              </button>
            </div>
          </div>
        </Bloque>
      )}

      {/* 2 · Método de pago — FUNCIONAL para clientes Rebill: "Actualizar medio de pago" pide a Rebill
          un link hosteado y redirige ahí (cambio de tarjeta, sin re-cobrar). Los de MP/otros no lo ven.
          La tarjeta guardada igual se ve arriba, en "Tu suscripción". Ver ADR-0024. */}
      {deEmpresa && esRebill && (
        <Bloque titulo="Método de pago" descripcion="Con la que se cobra tu plan." icono={<CreditCard className="h-[18px] w-[18px]" />}>
          {tarjetaLabel ? (
            <div className="flex items-center gap-3">
              <CardBrandIcon brand={brandGuardada} />
              <div>
                <p className="text-[14px] text-brand-black/90">{tarjetaLabel}</p>
                {metodo?.venc && <p className="text-[12.5px] text-neutral-500">Vence el {metodo.venc}.</p>}
              </div>
            </div>
          ) : (
            <p className="text-[14px] text-neutral-500 mb-1">No tenés un método de pago guardado.</p>
          )}
          <button
            onClick={actualizarTarjeta}
            disabled={actualizandoTarjeta}
            className="inline-flex items-center gap-1.5 mt-3 text-[14px] font-medium text-brand-primary hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-default"
          >
            {actualizandoTarjeta ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Pencil className="w-3.5 h-3.5" />
            )}
            {tarjetaLabel ? 'Actualizar medio de pago' : 'Agregar medio de pago'}
          </button>
        </Bloque>
      )}

      {/* 3 · Datos de facturación (fiscales) */}
      <Bloque titulo="Datos de facturación" descripcion="Para emitir las facturas AFIP de tu plan." icono={<Receipt className="h-[18px] w-[18px]" />}>
        {datos ? (
          <div>
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-success-soft px-3.5 py-2.5 text-[13px] font-medium text-success-text">
              <CheckCircle2 className="h-4 w-4 shrink-0" /> Facturación activada: tus facturas se generan solas con cada pago.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="CUIT" value={formatCUIT(datos.cuit)} />
              <Field label="Razón social" value={datos.razon_social} />
              <Field label="Condición IVA" value={datos.condicion_iva} />
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={openForm}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[14px] font-medium text-brand-primary hover:opacity-80 transition-opacity"
              >
                <Pencil className="w-4 h-4" /> Actualizar datos
              </button>
            </div>
          </div>
        ) : (
          <div className="py-3 text-center">
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary">
              <Receipt className="h-6 w-6" />
            </span>
            <p className="mb-1 text-[15px] font-semibold text-brand-black">Configurá tus datos de facturación</p>
            <p className="mx-auto mb-4 max-w-sm text-[13.5px] leading-relaxed text-neutral-500">
              Cargá tu CUIT y razón social para recibir la Factura A de tu plan y descontar el IVA.
            </p>
            <button onClick={openForm} className={BTN_PRIMARY}>
              Configurar facturación
            </button>
          </div>
        )}
      </Bloque>

      {/* 4 · Historial de facturas */}
      <Bloque
        titulo="Historial de facturas"
        descripcion={bloque === 'marcas' ? 'Las facturas de cada pago de los planes de tus marcas.' : 'Las facturas de cada pago de tu plan.'}
        icono={<FileText className="h-[18px] w-[18px]" />}
        accion={
          facturas.length > 0 || q ? (
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar"
                className="h-9 w-32 sm:w-44 pl-8 pr-2 rounded-xl border border-neutral-200 bg-surface-card text-[13px] outline-none focus:border-brand-primary transition-colors"
              />
            </div>
          ) : undefined
        }
      >
        {facturas.length === 0 ? (
          <div className="py-4 text-center">
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-neutral-100 text-neutral-400">
              <FileText className="h-6 w-6" />
            </span>
            <p className="mb-1 text-[15px] font-semibold text-brand-black">
              {q.trim() ? 'Sin resultados' : 'Todavía no tenés facturas'}
            </p>
            {!q.trim() && (
              <p className="text-[13px] text-neutral-500">
                {datos
                  ? 'Tu próxima factura va a aparecer acá con el próximo pago.'
                  : 'Activá tu facturación (arriba) para empezar a recibir tus facturas.'}
              </p>
            )}
          </div>
        ) : (
          <ul className="-my-1 divide-y divide-neutral-100">
            {facturas.map((f) => (
              <li key={f.id}>
                <button
                  onClick={() => navigate(`/facturacion/factura/${f.id}${demo ? '?demo' : ''}`)}
                  className="w-full flex items-center gap-3 py-3 text-left hover:bg-neutral-50 -mx-2 px-2 rounded-xl transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-brand-black/90 font-medium">{fmtFecha(f.fecha)}</p>
                    <p className="text-[12.5px] text-neutral-500">
                      Factura {f.tipo}{f.plan ? ` · Plan ${f.plan}` : ` · N° ${f.numero}`}{f.marca ? ` · ${f.marca}` : ''}
                    </p>
                  </div>
                  <span className="text-[14px] text-brand-black/90 tabular-nums">{MONEDA.format(f.total)}</span>
                  <span
                    className={`text-[11.5px] font-medium px-2 py-0.5 rounded-full ${
                      f.estado === 'emitida' ? 'bg-success-soft text-success-text' : 'bg-warning-soft text-warning-text'
                    }`}
                  >
                    {f.estado === 'emitida' ? 'Pagada' : 'Procesando'}
                  </span>
                  <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Bloque>

      {/* Modal: form de datos fiscales — REAL (guarda a Supabase) */}
      <OptionSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onAccept={guardar}
        title="Datos de facturación"
        acceptLabel={saving ? 'Guardando…' : 'Guardar'}
        acceptDisabled={!formValido || saving}
        closeOnAccept={false}
      >
        <div className="flex flex-col gap-4 p-1">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-brand-black/80">CUIT</span>
            <input
              value={cuit}
              onChange={(e) => setCuit(e.target.value)}
              inputMode="numeric"
              placeholder="30123456789"
              className={`h-11 px-3 rounded-xl border text-[15px] outline-none transition-colors ${
                cuit && !cuitValido
                  ? 'border-red-400'
                  : 'border-neutral-200 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.12)]'
              }`}
            />
            {cuit && !cuitValido && <span className="text-[12px] text-red-500">CUIT inválido (11 dígitos).</span>}
            {cuitValido && padron === 'buscando' && (
              <span className="text-[12px] text-neutral-400">Buscando en AFIP…</span>
            )}
            {cuitValido && padron === 'ok' && (
              <span className="text-[12px] text-success-text">✓ Encontrado en AFIP — completamos tus datos.</span>
            )}
            {cuitValido && padron === 'no' && (
              <span className="text-[12px] text-amber-600">Ese CUIT no figura en el padrón de AFIP.</span>
            )}
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-brand-black/80">Razón social</span>
            <input
              value={razonSocial}
              onChange={(e) => setRazonSocial(e.target.value)}
              placeholder="Tu nombre o el de tu empresa"
              className="h-11 px-3 rounded-xl border border-neutral-200 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.12)] text-[15px] outline-none transition-colors"
            />
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-brand-black/80">Condición frente al IVA</span>
            <CustomSelect
              value={condicionIva}
              onChange={setCondicionIva}
              options={CONDICIONES_IVA.map((c) => ({ value: c, label: c }))}
            />
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-brand-black/80">
              Email de facturación <span className="text-neutral-400 font-normal">(opcional)</span>
            </span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="facturas@tuempresa.com"
              className="h-11 px-3 rounded-xl border border-neutral-200 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.12)] text-[15px] outline-none transition-colors"
            />
          </label>
        </div>
      </OptionSheet>

    </div>
  );
}

const BTN_PRIMARY =
  'h-10 px-4 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-[0_10px_20px_-12px_rgb(var(--brand-primary-rgb)_/_0.8)] transition-colors whitespace-nowrap';
const BTN_OUTLINE =
  'h-10 px-4 rounded-xl border border-neutral-200 hover:bg-neutral-50 text-neutral-700 text-[14px] font-medium transition-colors whitespace-nowrap';

/**
 * Una sección del portal: la misma card que las secciones de Tienda online (`lanzamientos/Seccion`)
 * — ícono en un chip, título y bajada adentro, y el contenido debajo
 * (ADR-2026-10-06-pantallas-post-pago-renovadas).
 */
function Bloque({
  titulo,
  icono,
  descripcion,
  accion,
  children,
}: {
  titulo: string;
  icono: ReactNode;
  descripcion?: string;
  accion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm">
      <header className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3.5 sm:px-5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-500">{icono}</span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[14.5px] font-semibold text-brand-black">{titulo}</h3>
          {descripcion && <p className="truncate text-[12.5px] text-neutral-500">{descripcion}</p>}
        </div>
        {accion}
      </header>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-neutral-50 px-3.5 py-2.5 ring-1 ring-black/[0.04]">
      <p className="text-[11.5px] font-medium uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-0.5 break-words text-[14px] font-medium text-brand-black">{value}</p>
    </div>
  );
}
